<template>
  <div class="pdp-wrapper">
    <v-data-table
      :headers="headers"
      :items="flatObjectiveRows"
      :items-per-page="-1"
      hide-default-footer
      dense
      class="pdp-table"
      :item-class="() => 'table-row'"
      item-key="rowKey"
    >
      <template v-slot:body="{ items }">
        <tbody>
          <tr
            v-for="row in items"
            :key="row.rowKey"
            :class="{
              'objective-row': true,
              'objective-row--hover': hoveredRowKey === row.rowKey,
              'objective-row--dragging': draggingRowKey === row.rowKey,
              'objective-row--drag-over': dragOverRowKey === row.rowKey
            }"
            :data-period-id="row.period && row.period.Period_ID ? row.period.Period_ID : ''"
            :data-objective-id="row.objective ? row.objective.Objective_ID : ''"
            @mouseenter="onObjectiveRowMouseEnter(row, $event)"
            @mouseleave="hoveredRowKey = null"
            @dragover.prevent="onObjectiveDragOver($event, row)"
            @dragleave="dragOverRowKey = null"
            @drop="onObjectiveDrop($event, row)"
          >
            <td
              v-if="row.isFirstObjective"
              :rowspan="row.objectiveCount"
              class="meeting-date-td"
            >
              <div class="meeting-date-cell">
                <span v-if="row.period.Meeting_Start_Time" class="meeting-date-text">{{ formatDateTime(row.period.Meeting_Start_Time) }}</span>
                <span v-else class="grey--text">—</span>
              </div>
            </td>
            <td
              v-if="row.isFirstObjective"
              :rowspan="row.objectiveCount"
              class="period-td"
            >
              <div class="period-cell">
                <div v-if="row.period.Period_ID" class="period-text">{{ formatPeriod(row.period.Start_Date, row.period.End_Date) }}</div>
                <div v-else class="period-text grey--text">No period</div>
                <div class="period-actions">
                  <v-icon
                    v-if="canEditPeriod(row.period) && row.period.Period_ID"
                    small
                    class="mr-1 grey--text action-icon"
                    @click="editPeriod(row.period)"
                  >
                    mdi-pencil-outline
                  </v-icon>
                  <v-icon
                    v-if="canEditPeriod(row.period) && !row.period.Period_ID"
                    small
                    class="mr-1 primary--text action-icon"
                    @click="addPeriodForMeeting(row.period)"
                  >
                    mdi-plus
                  </v-icon>
                </div>
              </div>
            </td>
            <td class="objectives-td">
              <div class="objectives-cell objectives-cell--row">
                <template v-if="!row.period.Period_ID">
                  <span class="grey--text caption">Create period first</span>
                </template>
                <template v-else-if="row.objective">
                  <v-icon
                    v-if="canEditPeriod(row.period)"
                    class="drag-handle mr-2 grey--text"
                    style="cursor: move;"
                    small
                    draggable="true"
                    @dragstart="onObjectiveDragStart($event, row)"
                    @dragend="onObjectiveDragEndClient"
                  >
                    mdi-drag
                  </v-icon>
                  <v-textarea
                    v-model="row.objective.Objective_Text"
                    :disabled="!canEditPeriod(row.period)"
                    :readonly="!canEditPeriod(row.period)"
                    dense
                    flat
                    hide-details
                    placeholder="Objective"
                    rows="1"
                    auto-grow
                    class="objective-input"
                    @blur="updateObjective(row.objective)"
                  ></v-textarea>
                  <div class="objectives-actions">
                    <v-icon
                      v-if="canEditPeriod(row.period)"
                      small
                      class="action-icon action-icon--delete error--text"
                      @click="deleteObjective(row.objective, row.period)"
                    >
                      mdi-delete-outline
                    </v-icon>
                    <span
                      v-if="row.isLastObjective && canEditPeriod(row.period) && row.period.Period_ID"
                      class="objectives-actions-divider"
                    />
                    <v-btn
                      v-if="row.isLastObjective && canEditPeriod(row.period) && row.period.Period_ID"
                      x-small
                      icon
                      class="add-objective-btn"
                      @click="showAddObjectiveDialog(row.period)"
                    >
                      <v-icon x-small>mdi-plus</v-icon>
                    </v-btn>
                  </div>
                </template>
                <template v-else>
                  <span class="grey--text caption">No objectives</span>
                  <v-btn
                    v-if="row.isLastObjective && canEditPeriod(row.period) && row.period.Period_ID"
                    x-small
                    icon
                    class="add-objective-btn ml-2"
                    @click="showAddObjectiveDialog(row.period)"
                  >
                    <v-icon x-small>mdi-plus</v-icon>
                  </v-btn>
                </template>
              </div>
            </td>
            <td class="type-td">
              <v-select
                v-if="row.objective"
                v-model="row.objective.Type"
                :items="typeOptions.filter(t => t !== 'Type')"
                :disabled="!canEditPeriod(row.period)"
                dense
                flat
                hide-details
                placeholder="Type"
                class="type-select"
                @change="updateObjective(row.objective)"
              >
                <template v-slot:item="{ item }">
                  <v-chip
                    :color="item === 'Main' ? 'primary' : 'orange'"
                    small
                    text-color="white"
                  >
                    {{ item }}
                  </v-chip>
                </template>
                <template v-slot:selection="{ item }">
                  <v-chip
                    :color="item === 'Main' ? 'primary' : 'orange'"
                    small
                    text-color="white"
                  >
                    {{ item }}
                  </v-chip>
                </template>
              </v-select>
              <span v-else class="grey--text caption">—</span>
            </td>
            <td class="means-td">
              <template v-if="row.objective">
                <v-textarea
                  v-if="canEditLimitedFields(row.period) && (!isLink(row.objective.Means) || editingMeansIds.includes(row.objective.Objective_ID))"
                  v-model="row.objective.Means"
                  dense
                  flat
                  hide-details
                  placeholder="Means"
                  rows="1"
                  auto-grow
                  class="means-input"
                  @blur="onMeansBlur(row.objective)"
                ></v-textarea>
                <span
                  v-else-if="isLink(row.objective.Means)"
                  class="means-link-wrapper"
                >
                  <a
                    :href="row.objective.Means"
                    target="_blank"
                    rel="noopener noreferrer"
                    style="color: #1976d2; text-decoration: underline; word-break: break-all;"
                  >{{ row.objective.Means }}</a>
                  <v-icon
                    v-if="canEditLimitedFields(row.period)"
                    small
                    class="means-edit-icon"
                    @click="editingMeansIds.push(row.objective.Objective_ID)"
                  >mdi-pencil</v-icon>
                </span>
                <span v-else class="means-text">{{ row.objective.Means }}</span>
              </template>
              <span v-else class="grey--text caption">—</span>
            </td>
            <td class="status-td">
              <v-select
                v-if="row.objective"
                v-model="row.objective.Status"
                :items="statusOptions.filter(s => s !== 'Status')"
                :disabled="!canEditLimitedFields(row.period)"
                dense
                flat
                hide-details
                placeholder="Status"
                class="status-select"
                @change="updateObjective(row.objective)"
              >
                <template v-slot:item="{ item }">
                  <v-chip
                    :color="getStatusColor(item)"
                    small
                    text-color="white"
                  >
                    {{ item }}
                  </v-chip>
                </template>
                <template v-slot:selection="{ item }">
                  <v-chip
                    :color="getStatusColor(item)"
                    small
                    text-color="white"
                  >
                    {{ item }}
                  </v-chip>
                </template>
              </v-select>
              <span v-else class="grey--text caption">—</span>
            </td>
            <td
              v-if="row.isFirstObjective"
              :rowspan="row.objectiveCount"
              class="progress-td"
            >
              <div class="progress-cell">
                <div class="progress-value">{{ calculateProgress(row.period) }}%</div>
                <div class="progress-details">
                  <span class="last-update">{{ formatLastUpdate(row.period) }}</span>
                  <span v-if="getAdditionalObjectivesDoneCount(row.period) > 0" class="trophy-icons">
                    <v-icon
                      v-for="n in getAdditionalObjectivesDoneCount(row.period)"
                      :key="`trophy-${n}`"
                      small
                      color="orange"
                      class="ml-1"
                    >
                      mdi-trophy
                    </v-icon>
                  </span>
                </div>
              </div>
            </td>
            <td
              v-if="row.isFirstObjective"
              :rowspan="row.objectiveCount"
              class="notes-td"
            >
              <div class="notes-cell">
                <div class="notes-content">
                  <div v-if="row.period.notes && row.period.notes.length > 0" class="notes-list">
                    <span
                      v-for="note in row.period.notes"
                      :key="note.Note_ID"
                      class="note-comment"
                      :class="{ 'note-comment--long': isNoteLong(note.Note_Text) }"
                      @click="isNoteLong(note.Note_Text) && openFullNoteModal(note)"
                    >
                      {{ truncateNote(note.Note_Text) }}
                      <v-icon
                        v-if="canEditLimitedFields(row.period)"
                        x-small
                        class="note-comment__delete"
                        @click.stop="deletePeriodNote(row.period, note)"
                      >
                        mdi-close
                      </v-icon>
                    </span>
                  </div>
                  <v-textarea
                    v-if="canEditLimitedFields(row.period) && row.period.Period_ID && addingNotePeriodId === row.period.Period_ID"
                    :ref="'noteInput-' + row.period.Period_ID"
                    :value="noteDrafts[row.period.Period_ID] || ''"
                    placeholder="Note"
                    rows="1"
                    auto-grow
                    dense
                    flat
                    solo
                    hide-details
                    class="notes-inline-input"
                    @input="setNoteDraft(row.period.Period_ID, $event)"
                    @blur="onNoteBlur(row.period)"
                    @keydown.enter.prevent="onNoteBlur(row.period)"
                  />
                  <div v-else-if="!row.period.notes || row.period.notes.length === 0" class="no-notes">No notes</div>
                </div>
                <v-btn
                  v-if="canEditLimitedFields(row.period) && row.period.Period_ID"
                  x-small
                  icon
                  class="add-note-btn"
                  @click="showNewNoteField(row.period)"
                >
                  <v-icon x-small>mdi-plus</v-icon>
                </v-btn>
              </div>
            </td>
          </tr>
        </tbody>
      </template>
    </v-data-table>

    <!-- Full note (long comment) dialog -->
    <v-dialog v-model="showFullNoteDialog" max-width="500" @click:outside="showFullNoteDialog = false">
      <v-card>
        <v-card-title class="text-subtitle-1">Comment</v-card-title>
        <v-card-text class="full-note-text">{{ fullNoteText }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showFullNoteDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Period Dialog -->
    <v-dialog v-model="showPeriodDialog" max-width="600">
      <v-card>
        <v-card-title>{{ editingPeriod ? 'Edit Period' : 'Add Period' }}</v-card-title>
        <v-card-text>
          <v-menu
            v-model="startDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="periodForm.startDate"
                label="Start Date"
                prepend-icon="mdi-calendar"
                readonly
                solo
                dense
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="periodForm.startDate"
              @input="startDateMenu = false"
            ></v-date-picker>
          </v-menu>
          <v-menu
            v-model="endDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="periodForm.endDate"
                label="End Date"
                prepend-icon="mdi-calendar"
                readonly
                solo
                dense
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="periodForm.endDate"
              @input="endDateMenu = false"
            ></v-date-picker>
          </v-menu>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closePeriodDialog">Cancel</v-btn>
          <v-btn color="primary" @click="savePeriod">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmOrDeniedDialog
      v-if="showDeleteObjectiveDialog"
      v-model="showDeleteObjectiveDialog"
      :mode="confirmOrDeniedDialogModes.delete"
      :message="'Are you sure you want to delete this objective?'"
      @onDeleteClick="confirmDeleteObjective"
      @onCancelClick="showDeleteObjectiveDialog = false"
      @onCloseClick="showDeleteObjectiveDialog = false"
    />
  </div>
</template>

<script>

export default {
  name: 'PdpTab',
  components: {
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue')
  },
  props: {
    employee: {
      required: true,
      type: Object
    },
    rolesOfUser: {
      required: false,
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      hoveredRowKey: null,
      editingMeansIds: [],
      headers: [
        { text: 'Meeting date', value: 'meetingDate', sortable: false, width: '80px', align: 'center' },
        { text: 'Period', value: 'period', sortable: false, width: '110px', align: 'center' },
        { text: 'Objectives', value: 'objectives', sortable: false, width: '300px', align: 'center' },
        { text: 'Type', value: 'type', sortable: false, width: '85px', align: 'center' },
        { text: 'Means', value: 'means', sortable: false, width: '270px', align: 'center' },
        { text: 'Status', value: 'status', sortable: false, width: '50px', align: 'center' },
        { text: 'Overall Progress', value: 'progress', sortable: false, width: '80px', align: 'center' },
        { text: 'Notes', value: 'notes', sortable: false, align: 'center' }
      ],
      periods: [],
      objectives: {},
      notes: {},
      editingPeriod: null,
      noteDrafts: {},
      addingNotePeriodId: null,
      showPeriodDialog: false,
      startDateMenu: false,
      endDateMenu: false,
      currentPeriod: null,
      typeOptions: ['Type', 'Main', 'Additional'],
      statusOptions: ['Status', 'Done', 'In Progress', 'Not Done', 'Not Applicable', 'On Hold'],
      periodForm: {
        startDate: null,
        endDate: null,
        meetingId: null
      },
      showDeleteObjectiveDialog: false,
      currentObjectiveToDelete: null,
      showFullNoteDialog: false,
      fullNoteText: '',
      noteTruncateLength: 90,
      draggingRowKey: null,
      dragOverRowKey: null
    }
  },
  computed: {
    user () {
      return this.$store.getters.getUser
    },
    isHRorFinancierOrManager () {
      return this.rolesOfUser.some(role => role.RoleName === 'HR' || role.RoleName === 'financier' || role.RoleName === 'manager')
    },
    isCurrentEmployee () {
      return this.employee && this.user && this.employee.Employee_ID === this.user.employee_id
    },
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    periodsWithObjectives () {
      return this.periods.map(period => {
        const periodId = period.Period_ID
        const periodObjectives = periodId ? (this.objectives[periodId] || []) : []
        const periodNotes = periodId ? (this.notes[periodId] || []) : []
        return {
          ...period,
          objectives: periodObjectives,
          notes: periodNotes
        }
      })
    },
    flatObjectiveRows () {
      const rows = []
      for (const period of this.periodsWithObjectives) {
        if (!period.Period_ID) {
          rows.push({
            rowKey: `period-${period.Meeting_ID || 'noperiod'}-no-period`,
            period,
            objective: null,
            isFirstObjective: true,
            isLastObjective: true,
            objectiveCount: 1
          })
          continue
        }
        const objectives = period.objectives || []
        if (objectives.length === 0) {
          rows.push({
            rowKey: `period-${period.Period_ID}-empty`,
            period,
            objective: null,
            isFirstObjective: true,
            isLastObjective: true,
            objectiveCount: 1
          })
        } else {
          objectives.forEach((objective, index) => {
            rows.push({
              rowKey: `period-${period.Period_ID}-obj-${objective.Objective_ID}`,
              period,
              objective,
              isFirstObjective: index === 0,
              isLastObjective: index === objectives.length - 1,
              objectiveCount: objectives.length
            })
          })
        }
      }
      return rows
    }
  },
  async mounted () {
    if (this.employee && this.employee.Employee_ID) {
      await this.loadPeriods()
    }
  },
  methods: {
    isLink (value) {
      if (!value) return false
      try {
        const url = new URL(value)
        return url.protocol === 'http:' || url.protocol === 'https:'
      } catch {
        return false
      }
    },
    onMeansBlur (objective) {
      const idx = this.editingMeansIds.indexOf(objective.Objective_ID)
      if (idx !== -1) this.editingMeansIds.splice(idx, 1)
      this.updateObjective(objective)
    },
    getDragPayload (event) {
      try {
        const raw = event.dataTransfer.getData('application/json')
        return raw ? JSON.parse(raw) : null
      } catch (_) {
        return null
      }
    },
    canDropObjective (source, row) {
      return row.objective && row.period &&
        String(source.periodId) === String(row.period.Period_ID) &&
        source.objectiveId !== row.objective.Objective_ID
    },
    onObjectiveDragStart (event, row) {
      if (!row.objective || !row.period || !row.period.Period_ID) return
      this.draggingRowKey = row.rowKey
      event.dataTransfer.setData('application/json', JSON.stringify({
        periodId: row.period.Period_ID,
        objectiveId: row.objective.Objective_ID
      }))
      event.dataTransfer.effectAllowed = 'move'
    },
    onObjectiveDragEndClient () {
      this.draggingRowKey = null
      this.dragOverRowKey = null
    },
    onObjectiveDragOver (event, row) {
      if (!this.draggingRowKey) return
      const source = this.getDragPayload(event)
      if (!source || !this.canDropObjective(source, row)) return
      event.dataTransfer.dropEffect = 'move'
      this.dragOverRowKey = row.rowKey
    },
    onObjectiveDrop (event, row) {
      this.draggingRowKey = null
      this.dragOverRowKey = null
      const source = this.getDragPayload(event)
      if (!source || !this.canDropObjective(source, row)) return
      event.preventDefault()
      const period = this.periodsWithObjectives.find(p => String(p.Period_ID) === String(source.periodId))
      if (!period || !period.objectives || !period.objectives.length) return
      const fromIdx = period.objectives.findIndex(o => o.Objective_ID === source.objectiveId)
      const toIdx = period.objectives.findIndex(o => o.Objective_ID === row.objective.Objective_ID)
      if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return
      const reordered = [...period.objectives]
      const [removed] = reordered.splice(fromIdx, 1)
      reordered.splice(toIdx, 0, removed)
      this.$set(this.objectives, period.Period_ID, reordered)
      this.saveObjectiveOrder(period, reordered)
    },
    async saveObjectiveOrder (period, reorderedObjectives) {
      const list = reorderedObjectives || period.objectives || []
      const headers = { Authorization: 'Bearer ' + this.user.token }
      try {
        for (let i = 0; i < list.length; i++) {
          const obj = list[i]
          if (obj.Order_Index === i) continue
          await this.axios.put(`/api/pdp/objectives/${obj.Objective_ID}`, {
            objectiveText: obj.Objective_Text,
            type: obj.Type,
            means: obj.Means,
            status: obj.Status,
            orderIndex: i
          }, { headers })
        }
        this.$store.dispatch('setSuccess', { successMsg: 'Порядок цілей збережено' })
      } catch (err) {
        this.$store.dispatch('setError', { errorMsg: 'Error updating objective order: ' + err.message })
        await this.loadObjectives(period.Period_ID)
      }
    },
    onObjectiveRowMouseEnter (row, event) {
      const td = event.target && event.target.closest && event.target.closest('td')
      if (!td) return
      const isSpannedCell = td.classList.contains('meeting-date-td') ||
        td.classList.contains('period-td') ||
        td.classList.contains('progress-td') ||
        td.classList.contains('notes-td')
      if (!isSpannedCell) this.hoveredRowKey = row.rowKey
    },
    canEditPeriod (period) {
      if (!period || !period.Meeting_Start_Time) return false
      return this.isHRorFinancierOrManager
    },
    canEditLimitedFields (period) {
      if (!period || !period.Meeting_Start_Time) return false
      const isOwnData = period.Employee_ID === this.user.employee_id
      return this.isHRorFinancierOrManager || (isOwnData && this.isCurrentEmployee)
    },
    getMeetingStatus (dateString) {
      if (!dateString) return ''
      const meetingDate = new Date(dateString)
      const now = new Date()
      return meetingDate >= now ? 'Upcoming' : 'Past'
    },
    async loadPeriods () {
      try {
        const response = await this.axios.get(`/api/pdp/periods/${this.employee.Employee_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        this.periods = response.data

        for (const period of this.periods) {
          if (period.Period_ID) {
            await this.loadObjectives(period.Period_ID)
            await this.loadPeriodNotes(period.Period_ID)
          }
        }
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message })
      }
    },
    async loadObjectives (periodId) {
      try {
        const response = await this.axios.get(`/api/pdp/periods/${periodId}/objectives`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        this.$set(this.objectives, periodId, response.data)
      } catch (error) {
        this.$set(this.objectives, periodId, [])
      }
    },
    async loadPeriodNotes (periodId) {
      try {
        const response = await this.axios.get(`/api/pdp/periods/${periodId}/notes`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        this.$set(this.notes, periodId, response.data)
      } catch (error) {
        this.$set(this.notes, periodId, [])
      }
    },
    calculateProgress (period) {
      const mainObjectives = period.objectives.filter(obj => obj.Type === 'Main')
      if (mainObjectives.length === 0) return 0

      const doneObjectives = mainObjectives.filter(obj => obj.Status === 'Done')
      return Math.round((doneObjectives.length / mainObjectives.length) * 100)
    },
    getAdditionalObjectivesDoneCount (period) {
      return period.objectives.filter(obj => obj.Type === 'Additional' && obj.Status === 'Done').length
    },
    formatLastUpdate (period) {
      if (!period.Updated_At) return ''
      const date = new Date(period.Updated_At)
      return `Last Update ${date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}`
    },
    formatDateTime (dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${day}/${month}/${year} ${hours}:${minutes}`
    },
    formatPeriod (startDate, endDate) {
      if (!startDate) return ''
      const start = new Date(startDate)
      const end = endDate ? new Date(endDate) : null
      const startDay = String(start.getDate()).padStart(2, '0')
      const startMonth = String(start.getMonth() + 1).padStart(2, '0')
      const startYear = start.getFullYear()
      const startStr = `${startDay}/${startMonth}/${startYear}`
      let endStr = ''
      if (end) {
        const endDay = String(end.getDate()).padStart(2, '0')
        const endMonth = String(end.getMonth() + 1).padStart(2, '0')
        const endYear = end.getFullYear()
        endStr = ` - ${endDay}/${endMonth}/${endYear}`
      }
      return startStr + endStr
    },
    getStatusColor (status) {
      const colors = {
        Done: 'green',
        'In Progress': 'amber',
        'Not Done': 'grey',
        'Not Applicable': 'purple',
        'On Hold': 'orange'
      }
      return colors[status] || 'grey'
    },
    async updateObjective (objective) {
      try {
        await this.axios.put(`/api/pdp/objectives/${objective.Objective_ID}`, {
          objectiveText: objective.Objective_Text,
          type: objective.Type,
          means: objective.Means,
          status: objective.Status,
          orderIndex: objective.Order_Index || 0
        }, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        this.$store.dispatch('setSuccess', { successMsg: 'Objective updated successfully' })
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message })
      }
    },
    async showAddObjectiveDialog (period) {
      try {
        await this.axios.post('/api/pdp/objectives', {
          periodId: period.Period_ID,
          objectiveText: '',
          type: 'Main',
          means: '',
          status: 'Not Done'
        }, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadObjectives(period.Period_ID)
        this.$store.dispatch('setSuccess', { successMsg: 'Objective added' })
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message })
      }
    },
    deleteObjective (objective, period) {
      this.currentObjectiveToDelete = { objective, period }
      this.showDeleteObjectiveDialog = true
    },
    async confirmDeleteObjective () {
      if (!this.currentObjectiveToDelete) return
      const { objective, period } = this.currentObjectiveToDelete
      try {
        await this.axios.delete(`/api/pdp/objectives/${objective.Objective_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadObjectives(period.Period_ID)
        this.$store.dispatch('setSuccess', { successMsg: 'Objective deleted successfully' })
        this.showDeleteObjectiveDialog = false
        this.currentObjectiveToDelete = null
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message })
      }
    },
    addPeriodForMeeting (meeting) {
      const meetingDate = new Date(meeting.Meeting_Start_Time || meeting.Start_Time)
      const startDate = new Date(meetingDate.getFullYear(), meetingDate.getMonth() + 1, 1)
      const endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 7, 0)
      this.periodForm = {
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        meetingId: meeting.Meeting_ID_From_Meeting || meeting.Meeting_ID
      }
      this.editingPeriod = null
      this.showPeriodDialog = true
    },
    async editPeriod (period) {
      this.editingPeriod = period
      this.periodForm = {
        startDate: period.Start_Date ? new Date(period.Start_Date).toISOString().split('T')[0] : null,
        endDate: period.End_Date ? new Date(period.End_Date).toISOString().split('T')[0] : null,
        meetingId: period.Meeting_ID
      }
      this.showPeriodDialog = true
    },
    closePeriodDialog () {
      this.showPeriodDialog = false
      this.editingPeriod = null
      this.periodForm = {
        startDate: null,
        endDate: null,
        meetingId: null
      }
    },
    async savePeriod () {
      if (!this.periodForm.startDate) {
        this.$store.dispatch('setError', { errorMsg: 'Start date is required' })
        return
      }

      try {
        if (this.editingPeriod) {
          await this.axios.put(`/api/pdp/periods/${this.editingPeriod.Period_ID}`, {
            startDate: this.periodForm.startDate,
            endDate: this.periodForm.endDate
          }, {
            headers: {
              Authorization: 'Bearer ' + this.user.token
            }
          })
          this.$store.dispatch('setSuccess', { successMsg: 'Period updated successfully' })
        } else {
          await this.axios.post('/api/pdp/periods', {
            meetingId: this.periodForm.meetingId,
            employeeId: this.employee.Employee_ID,
            startDate: this.periodForm.startDate,
            endDate: this.periodForm.endDate
          }, {
            headers: {
              Authorization: 'Bearer ' + this.user.token
            }
          })
          this.$store.dispatch('setSuccess', { successMsg: 'Period added successfully' })
        }
        this.closePeriodDialog()
        await this.loadPeriods()
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message })
      }
    },
    setNoteDraft (periodId, value) {
      this.$set(this.noteDrafts, periodId, value)
    },
    showNewNoteField (period) {
      this.addingNotePeriodId = period.Period_ID
      this.$set(this.noteDrafts, period.Period_ID, '')
      this.$nextTick(() => {
        const ref = this.$refs['noteInput-' + period.Period_ID]
        const comp = Array.isArray(ref) ? ref[0] : ref
        if (comp && comp.$el) {
          const textarea = comp.$el.querySelector('textarea')
          if (textarea) textarea.focus()
        }
      })
    },
    onNoteBlur (period) {
      const text = (this.noteDrafts[period.Period_ID] || '').trim()
      this.addingNotePeriodId = null
      if (!text) {
        this.$set(this.noteDrafts, period.Period_ID, '')
        return
      }
      this.currentPeriod = period
      this.addPeriodNote(text)
      this.$set(this.noteDrafts, period.Period_ID, '')
    },
    async addPeriodNote (noteText) {
      const text = typeof noteText === 'string' ? noteText.trim() : ''
      if (!text) return
      if (!this.currentPeriod || !this.currentPeriod.Period_ID) {
        this.$store.dispatch('setError', { errorMsg: 'Period must be created first' })
        return
      }

      try {
        await this.axios.post(`/api/pdp/periods/${this.currentPeriod.Period_ID}/notes`, {
          noteText: text
        }, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadPeriodNotes(this.currentPeriod.Period_ID)
        this.$store.dispatch('setSuccess', { successMsg: 'Note added successfully' })
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message })
      }
    },
    async deletePeriodNote (period, note) {
      try {
        await this.axios.delete(`/api/pdp/periods/notes/${note.Note_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadPeriodNotes(period.Period_ID)
        this.$store.dispatch('setSuccess', { successMsg: 'Note deleted successfully' })
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message })
      }
    },
    truncateNote (text) {
      if (!text || typeof text !== 'string') return ''
      if (text.length <= this.noteTruncateLength) return text
      return text.slice(0, this.noteTruncateLength).trim() + '...'
    },
    isNoteLong (text) {
      return text && typeof text === 'string' && text.length > this.noteTruncateLength
    },
    openFullNoteModal (note) {
      this.fullNoteText = note.Note_Text || ''
      this.showFullNoteDialog = true
    }
  }
}
</script>

<style scoped>
.pdp-wrapper {
  padding: 16px;
}

.pdp-table {
  background: var(--v-surface-base);
  border-radius: 4px;
}

.pdp-table >>> .v-data-table__wrapper {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}

.theme--dark .pdp-table >>> .v-data-table__wrapper {
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.pdp-table >>> table {
  border-collapse: collapse;
}

.pdp-table >>> table tbody td {
  border-right: 1px solid rgba(0, 0, 0, 0.08) !important;
}

.theme--dark .pdp-table >>> table tbody td {
  border-right: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.pdp-table >>> table tbody td.notes-td {
  padding: 4px !important;
  border-right: none !important;
}

.pdp-table >>> table thead th {
  border-right: 1px solid rgba(0, 0, 0, 0.12) !important;
}

.theme--dark .pdp-table >>> table thead th {
  border-right: 1px solid rgba(255, 255, 255, 0.12) !important;
}

.pdp-table >>> table thead th:last-child {
  border-right: none !important;
}

.pdp-table >>> tbody tr {
  background: transparent !important;
}

.pdp-table >>> tbody tr:hover {
  background: transparent !important;
}

.pdp-table >>> tbody tr:hover td {
  background-color: #eeeeee !important;
}

.theme--dark .pdp-table >>> tbody tr:hover td {
  background-color: #616161 !important;
}

.pdp-table >>> tbody tr:hover td.meeting-date-td,
.pdp-table >>> tbody tr:hover td.period-td,
.pdp-table >>> tbody tr:hover td.progress-td,
.pdp-table >>> tbody tr:hover td.notes-td {
  background: transparent !important;
}

.table-row {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.theme--dark .table-row {
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.objective-row td {
  padding: 2px !important;
  vertical-align: middle !important;
  text-align: center;
}

.meeting-date-td,
.period-td,
.progress-td,
.notes-td {
  vertical-align: middle !important;
}

.meeting-date-td .meeting-date-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 4px 0;
}

.period-td .period-cell {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 4px 0;
  gap: 4px;
}

.period-text {
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.4;
}

.objectives-td {
  text-align: center;
}

.objectives-cell--row {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 0;
}

.objectives-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px 0;
}

.period-actions {
  display: flex;
  align-items: center;
}

.objectives-content-wrapper {
  flex: 1;
  min-width: 0;
}

.objectives-actions .action-icon {
  cursor: pointer;
  opacity: 0.6;
}

.objectives-actions .action-icon:hover {
  opacity: 1;
}

.objectives-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.objectives-actions-divider {
  width: 1px;
  height: 16px;
  background: rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
}

.theme--dark .objectives-actions-divider {
  background: rgba(255, 255, 255, 0.3);
}

.add-objective-btn {
  margin-left: 0;
}

.type-cell,
.means-cell,
.status-cell {
  padding: 8px 0;
}

.notes-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
}

.notes-content {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;
}

.objective-item {
  display: flex;
  align-items: baseline;
}

.objective-item:not(:last-child) {
  margin-bottom: 4px;
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.theme--dark .objective-item {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.objective-text {
  flex: 1;
  font-size: 0.875rem;
}

.type-select,
.status-select {
  font-size: 0.875rem;
}

.type-select >>> .v-input__control,
.status-select >>> .v-input__control {
  min-height: 24px !important;
  height: 24px !important;
}

.type-select >>> .v-input__append-inner,
.status-select >>> .v-input__append-inner {
  padding: 0 !important;
}

.type-select >>> .v-input__slot,
.status-select >>> .v-input__slot {
  margin-bottom: 0 !important;
  padding: 0 !important;
  min-height: 24px !important;
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
}

.type-select >>> .v-input__slot::before,
.type-select >>> .v-input__slot::after,
.status-select >>> .v-input__slot::before,
.status-select >>> .v-input__slot::after {
  display: none !important;
}

.type-select >>> .v-select__selections,
.status-select >>> .v-select__selections {
  padding-left: 0;
}

.type-select >>> .v-select__selections .v-select__selection--comma,
.status-select >>> .v-select__selections .v-select__selection--comma {
  padding-left: 0;
}

.objective-input,
.means-input {
  font-size: 0.875rem;
}

.objective-input >>> .v-input__control,
.means-input >>> .v-input__control {
  min-height: 24px !important;
  height: auto !important;
}

.objective-input >>> .v-input__slot,
.means-input >>> .v-input__slot {
  margin-bottom: 0 !important;
  padding: 0 4px !important;
  min-height: 24px !important;
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
}

.objective-input >>> .v-input__slot::before,
.objective-input >>> .v-input__slot::after,
.means-input >>> .v-input__slot::before,
.means-input >>> .v-input__slot::after {
  display: none !important;
}

.objective-input >>> .v-text-field__slot input,
.objective-input >>> textarea,
.means-input >>> .v-text-field__slot input,
.means-input >>> textarea {
  padding: 0 !important;
}

.objective-input >>> textarea,
.means-input >>> textarea {
  line-height: 1.5 !important;
  padding: 6px 0 !important;
  white-space: pre-wrap !important;
  word-wrap: break-word !important;
  overflow-wrap: break-word !important;
}

.type-td >>> .v-input,
.means-td >>> .v-input,
.status-td >>> .v-input {
  margin: 0 auto;
  max-width: 100%;
}

.means-link-wrapper {
  display: inline-flex;
  align-items: flex-start;
  gap: 4px;
}

.means-edit-icon {
  opacity: 0;
  transition: opacity 0.15s;
  flex-shrink: 0;
  margin-top: 2px;
  cursor: pointer;
}

.means-td:hover .means-edit-icon {
  opacity: 1;
}

.means-text {
  white-space: pre-wrap;
  word-break: break-word;
}

.type-td >>> .v-input,
.status-td >>> .v-input {
  width: fit-content !important;
  min-width: 0;
}

.type-td >>> .v-input__slot,
.type-td >>> .v-select__selections,
.status-td >>> .v-input__slot,
.status-td >>> .v-select__selections {
  display: flex !important;
  justify-content: center !important;
}

.type-td >>> .v-select__selections input,
.status-td >>> .v-select__selections input {
  width: 0 !important;
  min-width: 0 !important;
  flex: 0 0 0 !important;
  padding: 0 !important;
  overflow: hidden !important;
}

.progress-td .progress-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.progress-cell {
  padding: 2px 0;
}

.progress-value {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 4px;
}

.progress-details {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  color: rgba(var(--v-theme-on-surface-rgb, 0, 0, 0), 0.6);
}

.theme--dark .progress-details {
  color: rgba(var(--v-theme-on-surface-rgb, 255, 255, 255), 0.7);
}

.last-update {
  font-size: 0.75rem;
}

.progress-details .trophy-icons {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 2px;
}

.notes-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 4px;
  min-width: 0;
  width: 100%;
}

.note-comment {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.875rem;
  color: var(--v-theme-on-surface);
  padding: 2px 0;
  margin-right: 8px;
  margin-bottom: 4px;
  max-width: 100%;
  min-width: 0;
}

.note-comment__text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.note-comment--long {
  cursor: pointer;
}

.note-comment--long:hover {
  opacity: 0.85;
}

.note-comment__delete {
  cursor: pointer;
  opacity: 0.6;
}

.note-comment__delete:hover {
  opacity: 1;
}

.full-note-text {
  white-space: pre-wrap;
  word-break: break-word;
}

.no-notes {
  font-size: 0.75rem;
  color: rgba(var(--v-theme-on-surface-rgb, 0, 0, 0), 0.5);
}

.theme--dark .no-notes {
  color: rgba(var(--v-theme-on-surface-rgb, 255, 255, 255), 0.6);
}

.add-note-btn {
  margin-left: 8px;
  flex-shrink: 0;
}

.notes-inline-input {
  margin-top: 4px;
  font-size: 0.875rem;
}

.notes-inline-input >>> .v-input__control {
  min-height: 28px !important;
}

.notes-inline-input >>> .v-input__slot {
  padding: 0 4px !important;
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
}

.notes-inline-input >>> .v-input__slot::before,
.notes-inline-input >>> .v-input__slot::after {
  display: none !important;
}

.action-icon {
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.action-icon:hover {
  opacity: 1;
}

.add-period-btn {
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.pdp-table >>> tbody tr.objective-row--dragging {
  opacity: 0.6;
}

.pdp-table >>> tbody tr.objective-row--drag-over {
  background-color: rgba(var(--v-theme-primary-rgb, 25, 118, 210), 0.08);
}

.theme--dark .pdp-table >>> tbody tr.objective-row--drag-over {
  background-color: rgba(var(--v-theme-primary-rgb, 25, 118, 210), 0.12);
}

</style>
